// Home — page d'accueil publique WYVIO (indexable).
// Positionnement : marque SaaS indépendante, plateforme de suivi complet
// de la relation client. Aucune mention d'un réseau, d'une concession
// ou d'un éditeur tiers dans le marketing — la seule mention éditeur
// est déportée dans le footer et les mentions légales.

import { Link } from "react-router-dom";
import {
  ArrowRight, Clock, ShieldCheck, FolderOpen,
  FileText, Camera, MessageSquare, CreditCard, Truck, LifeBuoy, Mail, Bell,
  Users,
} from "lucide-react";
import SEOHead from "../SEOHead";
import { seoConfig, jsonLdOrganization, jsonLdSoftwareApplication, jsonLdWebsite } from "../seoConfig";

const P = seoConfig.pages.home;

const Section = ({ children, className = "", testId }) => (
  <section
    data-testid={testId}
    className={`mx-auto max-w-6xl px-6 lg:px-10 ${className}`}
  >
    {children}
  </section>
);

const Home = () => {
  return (
    <>
      <SEOHead
        title={P.title}
        description={P.description}
        keywords={P.keywords}
        path={P.path}
        jsonLd={[jsonLdOrganization(), jsonLdWebsite(), jsonLdSoftwareApplication()]}
      />

      {/* HERO — moins de texte, plus de respiration, mockup riche */}
      <Section testId="hero" className="pt-24 pb-32 lg:pt-32 lg:pb-40">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.05fr] gap-16 lg:gap-20 items-center">
          {/* Colonne gauche — promesse condensée */}
          <div>
            <p
              className="inline-flex items-center gap-2 rounded-full bg-[#F0FFDE] text-[#4a6b0f] text-[12px] font-medium px-3 py-1.5 mb-8"
              data-testid="hero-eyebrow"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#8FB600]" />
              WYVIO
            </p>

            <h1
              className="text-[42px] sm:text-[52px] lg:text-[64px] font-semibold tracking-[-0.035em] leading-[1.02] text-[#111]"
              data-testid="hero-title"
            >
              Toute votre relation<br />client.<br />
              <span className="text-[#6B7280]">Une seule plateforme.</span>
            </h1>

            <ul className="mt-10 space-y-3" data-testid="hero-promise-list">
              {[
                "Gagnez du temps.",
                "Sécurisez vos échanges.",
                "Gardez une traçabilité complète.",
              ].map((p) => (
                <li key={p} className="flex items-center gap-3 text-[17px] lg:text-[18px] text-[#111] font-medium">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#8FB600]" />
                  {p}
                </li>
              ))}
            </ul>

            <div className="mt-11 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="h-12 inline-flex items-center gap-2 rounded-full bg-[#111] text-white text-[14px] font-medium px-6 hover:brightness-110 transition-colors"
              >
                Demander une démonstration
                <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
              <Link
                to="/fonctionnalites"
                data-testid="hero-cta-secondary"
                className="h-12 inline-flex items-center gap-2 rounded-full border border-black/10 text-[#111] text-[14px] font-medium px-6 hover:bg-[#F7F8FA] transition-colors"
              >
                Découvrir WYVIO
              </Link>
            </div>

            <p className="mt-10 text-[14px] text-[#6B7280] leading-[1.65] max-w-[440px]" data-testid="hero-tagline">
              Une plateforme conçue pour simplifier et sécuriser la relation client
              des professionnels de l&apos;automobile. Chaque échange, chaque document et chaque étape
              sont automatiquement historisés — pour que votre équipe ne perde plus jamais une information.
            </p>
          </div>

          {/* Colonne droite — mockup riche "toute la vie d'un dossier" */}
          <div className="relative" data-testid="hero-visual">
            <div aria-hidden className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#C8FF2F]/25 blur-3xl" />
            <div aria-hidden className="absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-[#C8FF2F]/10 blur-3xl" />

            {/* Card principale — timeline riche */}
            <div className="relative rounded-3xl bg-[#111] text-white p-8 lg:p-9 shadow-2xl shadow-black/15">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-[#C8FF2F]">
                    Dossier BC-2026-1042
                  </p>
                  <h3 className="mt-2.5 text-[22px] font-semibold tracking-tight">
                    Audi Q5 · 45 TFSI e
                  </h3>
                  <p className="mt-1 text-[12px] text-white/50">
                    Mme Durand · livraison prévue jeudi
                  </p>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C8FF2F]/15 text-[#C8FF2F] text-[10.5px] font-semibold px-2.5 py-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C8FF2F] animate-pulse" />
                  Actif
                </span>
              </div>

              {/* Compteur discret */}
              <p className="mt-6 text-[11px] uppercase tracking-[0.18em] text-white/40">
                Historique · 47 événements
              </p>

              {/* Timeline riche — 8 événements, variétés de types */}
              <div className="mt-5 space-y-2.5">
                {[
                  { Icon: Truck,          label: "Livraison confirmée",         time: "il y a 2 h",  color: "#C8FF2F" },
                  { Icon: Camera,         label: "4 photos ajoutées",           time: "il y a 4 h",  color: "#C8FF2F" },
                  { Icon: MessageSquare,  label: "SMS reçu · client",           time: "il y a 6 h",  color: "#C8FF2F" },
                  { Icon: FileText,       label: "CPI validée",                 time: "hier",         color: "#C8FF2F" },
                  { Icon: CreditCard,     label: "Acompte 1 500 € reçu",        time: "hier",         color: "#C8FF2F" },
                  { Icon: Mail,           label: "Email confirmation envoyé",   time: "il y a 2 j",  color: "#C8FF2F" },
                  { Icon: ShieldCheck,    label: "Contrôle technique OK",       time: "il y a 3 j",  color: "#C8FF2F" },
                  { Icon: LifeBuoy,       label: "Cas SAV clôturé",             time: "il y a 5 j",  color: "white/40" },
                ].map(({ Icon, label, time }) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 rounded-xl bg-white/[0.05] px-3 py-2.5"
                  >
                    <div className="h-7 w-7 rounded-lg bg-white/[0.06] flex items-center justify-center shrink-0">
                      <Icon size={13} strokeWidth={1.9} className="text-white/85" />
                    </div>
                    <p className="text-[12.5px] text-white/85 flex-1 truncate">{label}</p>
                    <span className="text-[10.5px] text-white/40 font-mono tabular-nums shrink-0">
                      {time}
                    </span>
                  </div>
                ))}
              </div>

              {/* Footer — accent lime */}
              <div className="mt-6 flex items-center gap-3 rounded-xl bg-[#C8FF2F] text-[#111] px-4 py-3">
                <FolderOpen size={15} strokeWidth={2} />
                <p className="text-[12.5px] font-semibold">
                  Toute la vie du dossier · centralisée.
                </p>
              </div>
            </div>

            {/* Petite card flottante — photo (tell the story visuel) */}
            <div className="hidden lg:block absolute -top-6 -left-10 w-32 rounded-2xl bg-white border border-black/5 p-2 shadow-xl shadow-black/10 rotate-[-4deg]">
              <div className="aspect-[4/3] rounded-xl bg-gradient-to-br from-[#F7F8FA] to-[#EEF2F5] flex items-center justify-center">
                <Camera size={18} strokeWidth={1.6} className="text-[#B0B5BC]" />
              </div>
              <p className="mt-2 px-1 text-[9.5px] uppercase tracking-widest text-[#6B7280]">
                Photo · aujourd&apos;hui
              </p>
            </div>

            {/* Petite card flottante — SMS bubble */}
            <div className="hidden lg:block absolute -bottom-6 -left-6 w-56 rounded-2xl bg-white border border-black/5 p-4 shadow-xl shadow-black/10 rotate-[3deg]">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-[#F0FFDE] flex items-center justify-center">
                  <MessageSquare size={11} strokeWidth={2} className="text-[#4a6b0f]" />
                </div>
                <p className="text-[10.5px] uppercase tracking-widest text-[#6B7280]">SMS · client</p>
              </div>
              <p className="mt-2.5 text-[12.5px] text-[#111] leading-snug">
                &laquo; Bonjour, je pourrai venir jeudi 14 h ? &raquo;
              </p>
              <p className="mt-1.5 text-[10px] font-mono text-[#B0B5BC]">Reçu il y a 6 h</p>
            </div>

            {/* Petite card flottante — notification */}
            <div className="hidden lg:block absolute -top-6 -right-4 w-52 rounded-2xl bg-white border border-black/5 p-4 shadow-xl shadow-black/10 rotate-[4deg]">
              <div className="flex items-center gap-2">
                <div className="h-6 w-6 rounded-full bg-[#F0FFDE] flex items-center justify-center">
                  <Bell size={11} strokeWidth={2} className="text-[#4a6b0f]" />
                </div>
                <p className="text-[10.5px] uppercase tracking-widest text-[#6B7280]">Notification</p>
              </div>
              <p className="mt-2.5 text-[12px] text-[#111] leading-snug font-medium">
                Livraison prête à confirmer.
              </p>
              <p className="mt-1.5 text-[10px] font-mono text-[#B0B5BC]">Auto · workflow</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Section 2 — 3 promesses seulement */}
      <Section testId="why" className="py-24 lg:py-32 border-t border-black/5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: Clock,
              title: "Gagnez du temps",
              body:  "Automatisez les tâches répétitives.",
            },
            {
              icon: ShieldCheck,
              title: "Sécurisez vos échanges",
              body:  "Chaque communication est conservée et horodatée.",
            },
            {
              icon: FolderOpen,
              title: "Traçabilité complète",
              body:  "Retrouvez instantanément tout l'historique d'un dossier.",
            },
          ].map(({ icon: Icon, title, body }) => (
            <article
              key={title}
              className="rounded-3xl border border-black/5 p-10 bg-[#F7F8FA]"
            >
              <div className="h-12 w-12 rounded-2xl bg-white flex items-center justify-center text-[#111] border border-black/5">
                <Icon size={20} strokeWidth={1.9} />
              </div>
              <h3 className="mt-8 text-[22px] font-semibold text-[#111] tracking-tight">{title}</h3>
              <p className="mt-3 text-[15px] text-[#6B7280] leading-[1.65]">{body}</p>
            </article>
          ))}
        </div>
      </Section>

      {/* Two audiences — reformulées "équipe / client" (aucune mention agence/concession) */}
      <Section testId="audiences" className="py-24 border-t border-black/5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-3xl bg-[#F7F8FA] p-10">
            <p className="text-[12px] font-semibold uppercase tracking-widest text-[#6B7280]">
              Côté équipe
            </p>
            <h3 className="mt-3 text-2xl font-semibold text-[#111] tracking-tight">
              Un centre de pilotage opérationnel.
            </h3>
            <ul className="mt-6 space-y-3">
              {[
                "Une vue unique de tous les dossiers en cours",
                "Alertes claires sur ce qui bloque une livraison",
                "Communications automatiques ou manuelles centralisées",
                "Historique complet consultable à tout moment",
              ].map((l) => (
                <li key={l} className="flex gap-3 text-[14px] text-[#111]">
                  <Users size={16} className="text-[#8FB600] mt-0.5" />
                  {l}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-[#111] text-white p-10 relative overflow-hidden">
            <div aria-hidden className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#C8FF2F]/20 blur-3xl" />
            <div className="relative">
              <p className="text-[12px] font-semibold uppercase tracking-widest text-[#C8FF2F]">
                Côté client
              </p>
              <h3 className="mt-3 text-2xl font-semibold tracking-tight">
                Un portail simple, rassurant.
              </h3>
              <ul className="mt-6 space-y-3">
                {[
                  "Statut de sa commande en un coup d'œil",
                  "Documents et photos disponibles au bon moment",
                  "Messagerie directe avec son interlocuteur",
                  "Aucune application à installer",
                ].map((l) => (
                  <li key={l} className="flex gap-3 text-[14px] text-white/90">
                    <Camera size={16} className="text-[#C8FF2F] mt-0.5" />
                    {l}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section testId="final-cta" className="py-32">
        <div className="rounded-3xl bg-[#111] text-white p-12 lg:p-16 relative overflow-hidden">
          <div aria-hidden className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#C8FF2F]/20 blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-[-0.02em]">
              Prêt à passer à WYVIO ?
            </h2>
            <p className="mt-4 text-[15px] text-white/70 leading-[1.65]">
              Une démonstration adaptée à votre activité, sans engagement.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/contact"
                data-testid="final-cta-btn"
                className="h-12 inline-flex items-center gap-2 rounded-full bg-[#C8FF2F] text-[#111] text-[14px] font-semibold px-6 hover:brightness-95 transition-colors"
              >
                Demander une démo
                <ArrowRight size={16} strokeWidth={2.5} />
              </Link>
              <Link
                to="/fonctionnalites"
                className="h-12 inline-flex items-center gap-2 rounded-full border border-white/15 text-white text-[14px] font-medium px-6 hover:bg-white/5 transition-colors"
              >
                Explorer les fonctionnalités
              </Link>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Home;
